import { useThree, useFrame } from "@react-three/fiber";
import { MutableRefObject, useState, CSSProperties } from "react";
import { Mesh, Vector2, Vector3 } from "three";
import { arrowTunnel } from "./arrow-tunnel";

type ArrowHUDProps = {
  targetRef: MutableRefObject<Mesh | null>;
  rectWidth?: number;
  rectHeight?: number;
  margin?: number;
  label: string;
};

type Style = {
  container: CSSProperties;
  arrow?: CSSProperties;
  label?: CSSProperties;
};

export function ArrowHUD({
  targetRef,
  rectWidth = 8,
  rectHeight = 6,
  margin = 20,
  label,
}: ArrowHUDProps) {
  const { camera, size } = useThree();
  const [style, setStyle] = useState<Style>({ container: { display: "none" } });

  function ndcToScreen(ndcX: number, ndcY: number) {
    const px = (ndcX * 0.5 + 0.5) * size.width;
    const py = (-ndcY * 0.5 + 0.5) * size.height;
    return new Vector2(px, py);
  }

  function clampPointToRectEdge(
    center: Vector2,
    point: Vector2,
    w: number,
    h: number,
    m: number
  ): Vector2 {
    const minX = m;
    const maxX = w - m;
    const minY = m;
    const maxY = h - m;
    const dir = new Vector2(point.x - center.x, point.y - center.y);

    if (Math.abs(dir.x) < 1e-9 && Math.abs(dir.y) < 1e-9) return center.clone();

    const ts = [];
    if (dir.x !== 0) {
      ts.push((minX - center.x) / dir.x, (maxX - center.x) / dir.x);
    }
    if (dir.y !== 0) {
      ts.push((minY - center.y) / dir.y, (maxY - center.y) / dir.y);
    }
    const positiveTs = ts.filter((t) => t > 0);
    if (positiveTs.length === 0) {
      return new Vector2(
        Math.min(Math.max(point.x, minX), maxX),
        Math.min(Math.max(point.y, minY), maxY)
      );
    }
    const t = Math.min(...positiveTs);
    return new Vector2(center.x + dir.x * t, center.y + dir.y * t);
  }

  const getLabelStyle = (clamped: Vector2) => {
    if (clamped.x >= size.width - margin - 1) {
      return {
        position: "absolute" as const,
        right: "100%",
        top: "50%",
        transform: "translateY(-50%)",
        marginRight: "8px",
        whiteSpace: "nowrap",
      };
    } else if (clamped.x <= margin + 1) {
      return {
        position: "absolute" as const,
        left: "100%",
        top: "50%",
        transform: "translateY(-50%)",
        marginLeft: "8px",
        whiteSpace: "nowrap",
      };
    } else if (clamped.y <= margin + 1) {
      return {
        position: "absolute" as const,
        top: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        marginTop: "8px",
        whiteSpace: "nowrap",
      };
    } else {
      return {
        position: "absolute" as const,
        bottom: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        marginBottom: "8px",
        whiteSpace: "nowrap",
      };
    }
  };

  useFrame(() => {
    if (!targetRef?.current) {
      setStyle({ container: { display: "none" } });
      return;
    }

    const corners = [
      new Vector3(-rectWidth / 2, -rectHeight / 2, 0),
      new Vector3(rectWidth / 2, -rectHeight / 2, 0),
      new Vector3(rectWidth / 2, rectHeight / 2, 0),
      new Vector3(-rectWidth / 2, rectHeight / 2, 0),
    ];

    let allLeft = true,
      allRight = true,
      allTop = true,
      allBottom = true;
    const worldCorner = new Vector3();
    const ndcCorner = new Vector3();

    for (const c of corners) {
      worldCorner.copy(c).applyMatrix4(targetRef.current.matrixWorld);
      ndcCorner.copy(worldCorner).project(camera);
      if (ndcCorner.x > -1) allLeft = false;
      if (ndcCorner.x < 1) allRight = false;
      if (ndcCorner.y > -1) allBottom = false;
      if (ndcCorner.y < 1) allTop = false;
    }

    const fullyOut = allLeft || allRight || allTop || allBottom;
    if (!fullyOut) {
      setStyle({ container: { display: "none" } });
      return;
    }

    const worldCenter = new Vector3();
    targetRef.current.getWorldPosition(worldCenter);
    const centerNDC = worldCenter.clone().project(camera);

    const screenPoint = ndcToScreen(centerNDC.x, centerNDC.y);
    const screenCenter = new Vector2(size.width / 2, size.height / 2);

    const clamped = clampPointToRectEdge(
      screenCenter,
      screenPoint,
      size.width,
      size.height,
      margin
    );

    const dir = new Vector2(
      screenPoint.x - screenCenter.x,
      screenPoint.y - screenCenter.y
    );

    if (centerNDC.z < 0) dir.multiplyScalar(-1);

    if (Math.abs(dir.x) < 1e-9 && Math.abs(dir.y) < 1e-9) {
      setStyle({ container: { display: "none" } });
      return;
    }

    const angle = Math.atan2(dir.y, dir.x) + Math.PI;

    setStyle({
      container: { left: `${clamped.x}px`, top: `${clamped.y}px` },
      arrow: { transform: `rotate(${angle}rad)` },
      label: getLabelStyle(clamped),
    });
  });

  return (
    <arrowTunnel.In>
      <div
        className="absolute pointer-events-none z-10 -translate-x-1/2 -translate-y-1/2 select-none"
        style={style.container}
      >
        <div
          className="text-3xl text-green-500 drop-shadow-md"
          style={style.arrow}
        >
          ➤
        </div>
        <span
          className="text-xs bg-green-600 text-black px-2 font-bold py-0.5 rounded-full whitespace-nowrap"
          style={style.label}
        >
          {label}
        </span>
      </div>
    </arrowTunnel.In>
  );
}
